import { useMemo, useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useFreshness, useKeyedWrite, useNow, useWrite } from "../api/hooks";
import type { Alert, Incident, LocationInput, LocationLink } from "../api/types";
import { can, hasAnyRole, ROLE } from "../auth/roles";
import { ActionDialog } from "../components/ActionDialog";
import { Badge, FlagBadges, StatusBadge } from "../components/Badges";
import { Dialog } from "../components/Dialog";
import { FileLinks } from "../components/Files";
import {
  ACCURACY_CLASS,
  ACTIVE_INCIDENT_STATUSES,
  ALERT_FLAG,
  ALERT_STATUS,
  CALLBACK_OUTCOME,
  CHANNEL,
  INCIDENT_STATUS,
  LOCATION_SOURCE,
  MESSAGE_CHANNEL,
  MESSAGE_PATH,
  MESSAGE_STATUS,
  SEVERITY,
  SEVERITY_ORDER,
  labelOf,
  optionsOf,
  statusOf,
} from "../components/labels";
import { Notice, ProblemAlert, QueryState, StaleNotice, UnknownOutcome, UnknownUnkeyed } from "../components/ProblemAlert";
import { useFireContext } from "../context";
import { formatDateTime, formatSeconds, lusakaLocalToUtc as lusakaToUtc, secondsBetween } from "../format/datetime";
import { FireMap } from "../map/FireMap";
import { Legend } from "../map/Legend";
import { pt, type MapCandidate, type MapData, type MapFix } from "../map/layers";
import { LocationPicker } from "./LocationPicker";
import { lonLat, metres, personLabel, reasonError, Row, useConfigNames, useWardNames } from "./shared";
import { Button } from "@lcc/ui";

const REOPEN_WINDOW_MS = 30 * 60_000;

/** Whether this person may reopen a handled alert now (UI only): a dispatcher or the chief always; its handler within 30 minutes. */
export function mayReopen(alert: Alert, principal: { roles: string[]; partyId: string }, now: Date): boolean {
  if (alert.status === "new") return false;
  if (hasAnyRole(principal, [ROLE.dispatcher, ROLE.chief])) return true;
  if (alert.handled_by !== principal.partyId || !alert.handled_at) return false;
  return now.getTime() - Date.parse(alert.handled_at) <= REOPEN_WINDOW_MS;
}

/**
 * The dismissal rule for an SOS from the app (UI only; the server enforces): a recorded call-back first; when every
 * attempt was `no_number`, the chief only.
 */
export function dismissRule(alert: Alert, principal: { roles: string[] }): "ok" | "callback_required" | "chief_required" {
  if (alert.channel !== "app_sos") return "ok";
  const outcomes = new Set(alert.callback_attempts.map((c) => c.outcome));
  if (outcomes.size === 0) return "callback_required";
  if (outcomes.size === 1 && outcomes.has("no_number") && !principal.roles.includes(ROLE.chief)) return "chief_required";
  return "ok";
}

// --------------------------------------------------------------------------------------------- confirmation

type LocationChoice = "alert" | "fix" | "other";
interface ConfirmForm {
  incidentType: string;
  severity: string;
  choice: LocationChoice;
  fixId: string;
  location: LocationInput | null;
  placeDescription: string;
  note: string;
}

function ConfirmDialog({ alert, onClose, onConfirmed, context }: { alert: Alert; onClose: () => void; onConfirmed: (i: Incident) => void; context: MapData }) {
  const names = useConfigNames();
  const types = names.staff?.incident_types ?? [];
  const write = useKeyedWrite((api, body: Record<string, unknown>, key) => endpoints.confirmAlert(api, alert.alert_id, body as never, key), `confirmAlert:${alert.alert_id}`);
  const kept = write.lockedForm as ConfirmForm | undefined;
  const initial: ConfirmForm = kept ?? {
    incidentType: alert.incident_type ?? "",
    severity: "",
    choice: alert.location ? "alert" : alert.fixes.length ? "fix" : "other",
    fixId: alert.fixes[0]?.fix_id ?? "",
    location: null,
    placeDescription: alert.place_description ?? "",
    note: "",
  };
  const [f, setF] = useState<ConfirmForm>(initial);
  const [err, setErr] = useState<string | null>(null);
  const set = <K extends keyof ConfirmForm>(k: K, v: ConfirmForm[K]) => setF((x) => ({ ...x, [k]: v }));
  const type = types.find((t) => t.key === f.incidentType);
  const locked = write.locked;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (locked) return write.mutate({}, { onSuccess: onConfirmed });
    if (!f.incidentType.trim()) return setErr("Choose the incident type.");
    const body: Record<string, unknown> = { incident_type: f.incidentType.trim() };
    if (f.severity) body.severity = f.severity;
    if (f.choice === "fix") {
      if (!f.fixId) return setErr("Choose which received position to use.");
      body.fix_id = f.fixId;
    } else if (f.choice === "other") {
      if (!f.location) return setErr("Search a place or drop a pin for the incident's location.");
      body.location = f.location;
    } else if (!alert.location) {
      return setErr("The alert has no location yet: choose a received position, search a place or drop a pin.");
    }
    if (f.placeDescription.trim()) body.place_description = f.placeDescription.trim();
    if (f.note.trim()) body.note = f.note.trim();
    setErr(null);
    write.remember(f);
    write.mutate(body, { onSuccess: onConfirmed });
  };

  return (
    <Dialog title={`Confirm alert ${alert.reference} as a new incident`} onClose={onClose} focusContainer={locked}>
      <form className="fer-form" onSubmit={submit} noValidate>
        <fieldset className="fer-bare-fieldset" disabled={locked}>
          <div className="fer-field">
            <label htmlFor="confirm-type">
              <span>Incident type (required)</span>
            </label>
            {types.length ? (
              <select id="confirm-type" value={f.incidentType} onChange={(e) => set("incidentType", e.target.value)}>
                <option value="">Choose…</option>
                {types.map((t) => (
                  <option key={t.key} value={t.key}>
                    {names.incidentType(t.key)}
                  </option>
                ))}
              </select>
            ) : (
              <input id="confirm-type" value={f.incidentType} maxLength={64} onChange={(e) => set("incidentType", e.target.value)} />
            )}
          </div>
          <div className="fer-field">
            <label htmlFor="confirm-severity">
              <span>Severity</span>
            </label>
            <select id="confirm-severity" value={f.severity} onChange={(e) => set("severity", e.target.value)}>
              <option value="">{type ? `The type's default (${statusOf(SEVERITY, type.default_severity).label})` : "The type's default"}</option>
              {SEVERITY_ORDER.map((s) => (
                <option key={s} value={s}>
                  {SEVERITY[s].label}
                </option>
              ))}
            </select>
          </div>
          <fieldset className="fer-fieldset">
            <legend>Location of the incident</legend>
            {alert.location && (
              <label className="fer-check">
                <input type="radio" name="loc" checked={f.choice === "alert"} onChange={() => set("choice", "alert")} />
                The alert's location ({labelOf(LOCATION_SOURCE, alert.location.source)}, within {metres(alert.location.accuracy_m)})
              </label>
            )}
            {alert.fixes.length > 0 && (
              <label className="fer-check">
                <input type="radio" name="loc" checked={f.choice === "fix"} onChange={() => set("choice", "fix")} />A received position
              </label>
            )}
            {f.choice === "fix" && (
              <select aria-label="Received position" value={f.fixId} onChange={(e) => set("fixId", e.target.value)}>
                {alert.fixes.map((x) => (
                  <option key={x.fix_id} value={x.fix_id}>
                    {labelOf(LOCATION_SOURCE, x.source)}, within {metres(x.accuracy_m)}, {formatDateTime(x.received_at)}
                  </option>
                ))}
              </select>
            )}
            <label className="fer-check">
              <input type="radio" name="loc" checked={f.choice === "other"} onChange={() => set("choice", "other")} />A place or a pin
            </label>
          </fieldset>
          {f.choice === "other" && <LocationPicker value={f.location} onChange={(l) => set("location", l)} disabled={locked} context={context} />}
          <label className="fer-field">
            <span>What the caller said about the place</span>
            <textarea rows={2} maxLength={500} value={f.placeDescription} onChange={(e) => set("placeDescription", e.target.value)} />
          </label>
          <label className="fer-field">
            <span>Note (shown on the incident's timeline)</span>
            <textarea rows={2} maxLength={2000} value={f.note} onChange={(e) => set("note", e.target.value)} />
          </label>
        </fieldset>
        {err && (
          <p className="fer-form-error" role="alert">
            {err}
          </p>
        )}
        <ProblemAlert error={write.error} />
        <UnknownOutcome write={write} retry={() => write.mutate({}, { onSuccess: onConfirmed })} />
        <div className="fer-actions">
          <Button variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button type="submit" disabled={write.isPending}>
            Confirm as a new incident
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// --------------------------------------------------------------------------------------------- location link

interface LinkForm {
  phone: string;
  language: string;
}

function LocationLinkDialog({ alert, onClose }: { alert: Alert; onClose: () => void }) {
  const write = useKeyedWrite((api, body: Record<string, unknown>, key) => endpoints.sendLocationLink(api, alert.alert_id, body as never, key), `sendLocationLink:${alert.alert_id}`);
  const kept = write.lockedForm as LinkForm | undefined;
  const [f, setF] = useState<LinkForm>(kept ?? { phone: "", language: "en" });
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<LocationLink | null>(null);
  const hasPhone = !!alert.caller.phone && !alert.caller.redacted_at;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (write.locked) return write.mutate({}, { onSuccess: setSent });
    const phone = f.phone.replace(/[\s-]/g, "");
    if (phone && !/^\+[1-9][0-9]{7,14}$/.test(phone)) return setErr("Type the number in international form, e.g. +260971234567.");
    if (!phone && !hasPhone) return setErr("This caller has no phone number: type the number to send the link to.");
    setErr(null);
    const body: Record<string, unknown> = { language: f.language };
    if (phone) body.phone = phone;
    write.remember(f);
    write.mutate(body, { onSuccess: setSent });
  };
  return (
    <Dialog title={`Send a location link for ${alert.reference}`} onClose={onClose} focusContainer={write.locked}>
      {sent ? (
        <div className="fer-form">
          <p role="status" className="fer-success">
            <span aria-hidden="true">✓ </span>
            Link sent to {sent.phone}. It works until {formatDateTime(sent.expires_at)}; positions it sends appear on this alert.
          </p>
          <div className="fer-actions">
            <Button onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      ) : (
        <form className="fer-form" onSubmit={submit} noValidate>
          <p>
            FER texts the caller a link to a page that asks their phone for its position. The page shows nothing about the
            alert.
          </p>
          <fieldset className="fer-bare-fieldset" disabled={write.locked}>
            <label className="fer-field">
              <span>{hasPhone ? "Send to another number (optional; default the caller's)" : "Phone number to text (required)"}</span>
              <input value={f.phone} maxLength={20} inputMode="tel" onChange={(e) => setF({ ...f, phone: e.target.value })} />
            </label>
            <label className="fer-field">
              <span>Language of the page</span>
              <select value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })}>
                <option value="en">English</option>
                <option value="ny">Nyanja</option>
                <option value="bem">Bemba</option>
              </select>
            </label>
          </fieldset>
          {err && (
            <p className="fer-form-error" role="alert">
              {err}
            </p>
          )}
          <ProblemAlert error={write.error} />
          <UnknownOutcome write={write} retry={() => write.mutate({}, { onSuccess: setSent })} />
          <div className="fer-actions">
            <Button variant="secondary" onClick={onClose}>
              Back
            </Button>
            <Button type="submit" disabled={write.isPending}>
              Send the link
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

// --------------------------------------------------------------------------------------------- the detail

type DialogKind = "confirm" | "link" | "linkIncident" | "dismiss" | "reopen" | "callback" | "setLocation" | null;

/**
 * One alert in full (`getAlert`; the caller is shown in full here only, and every view is audited). The call-taker
 * claims it, sets its location (a received position, a place or a pin), sends a location link, records call-backs,
 * checks the duplicate candidates on the map, and confirms, links or dismisses it; a handled alert can be reopened.
 */
export function AlertDetail({ alertId, onClose, onOpenAlert }: { alertId: string; onClose: () => void; onOpenAlert: (id: string) => void }) {
  const { principal, go, refreshMs } = useFireContext();
  const now = useNow();
  const names = useConfigNames();
  const wardName = useWardNames();
  const q = useApi(["alert", alertId], (api) => endpoints.getAlert(api, alertId), { refetchInterval: refreshMs });
  const live = useApi(["live"], endpoints.getLiveMap, { enabled: can(principal, "viewLiveMap"), refetchInterval: refreshMs });
  const incidents = useApi(["incidents", { active: true }], (api) => endpoints.listIncidents(api), { enabled: can(principal, "viewIncidents") });
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [linkTo, setLinkTo] = useState<string>("");
  const claim = useWrite((api, release: boolean) => endpoints.claimAlert(api, alertId, { release }));
  // Claiming re-reads the alert first (review FW3; the contract has no conditional claim): a claim someone made since
  // this view was drawn is never taken over silently.
  const [takeover, setTakeover] = useState<{ name: string } | null>(null);
  const [checking, setChecking] = useState(false);
  // A duplicate candidate picked on the map (review FW4): an alert opens as an alert; an incident is offered for linking
  // (a new alert) or opens on the dispatch board. An incident id is never fetched as an alert.
  const pickCandidate = (id: string) => {
    const c = q.data?.duplicate_candidates.find((x) => x.id === id);
    if (!c) return;
    if (c.kind === "alert") return onOpenAlert(c.id);
    if (q.data?.status === "new" && can(principal, "linkAlert")) return openLink(c.id);
    if (can(principal, "viewIncidents")) go("board", { incidentId: c.id });
  };
  // The link dialog always offers the chosen incident (review FW5): the incident list is re-read when it opens.
  const openLink = (incidentId: string) => {
    setLinkTo(incidentId);
    setDialog("linkIncident");
    void incidents.refetch();
  };
  const startClaim = async () => {
    setChecking(true);
    try {
      const r = await q.refetch();
      const cur = r.data;
      if (!cur || r.error || cur.status !== "new") return;
      if (cur.handler_id && cur.handler_id !== principal.partyId) setTakeover({ name: personLabel(cur.handler_id, principal.partyId, cur.handler_name) });
      else if (!cur.handler_id) claim.mutate(false);
    } finally {
      setChecking(false);
    }
  };
  const useFix = useWrite((api, fixId: string) => endpoints.setAlertLocation(api, alertId, { fix_id: fixId }));
  const a = q.data;

  const mapData: MapData = useMemo(() => {
    if (!a) return {};
    const fixes: MapFix[] = a.fixes.map((x) => ({
      id: x.fix_id,
      point: [x.lon, x.lat],
      accuracyM: x.accuracy_m,
      label: labelOf(LOCATION_SOURCE, x.source),
      chosen: a.location?.fix_id === x.fix_id,
    }));
    if (a.location && !a.location.fix_id) fixes.push({ id: "chosen", point: [a.location.lon, a.location.lat], accuracyM: a.location.accuracy_m, label: "Chosen location", chosen: true });
    const candidates: MapCandidate[] = [];
    for (const c of a.duplicate_candidates) {
      const hit = c.kind === "incident" ? live.data?.incidents.find((i) => i.incident_id === c.id) : live.data?.alerts.find((x) => x.alert_id === c.id);
      const p = hit ? pt(hit.lon, hit.lat) : null;
      if (p) candidates.push({ id: c.id, kind: c.kind, reference: c.reference, point: p });
    }
    return { fixes, candidates, wards: undefined };
  }, [a, live.data]);

  const fresh = useFreshness(q);
  if (!a) return <QueryState isLoading={q.isLoading} error={q.error} label="the alert" />;

  const isNew = a.status === "new";
  // A stale alert's clock stops at its last refresh (review FW1): it never claims to be live.
  const clockNow = fresh.stale && fresh.refreshedAt ? fresh.refreshedAt : now;
  const waited = secondsBetween(a.received_at, a.handled_at ?? clockNow);
  const rule = dismissRule(a, principal);
  const activeIncidents = (incidents.data ?? []).filter((i) => (ACTIVE_INCIDENT_STATUSES as readonly string[]).includes(i.status) && i.status !== "cleared");
  const onMap = new Set(mapData.candidates?.map((c) => c.id));
  // The incidents offered for linking: the active ones, plus the chosen duplicate candidate when the list does not have
  // it yet (review FW5: what is sent is what is shown).
  const chosen = linkTo ? a.duplicate_candidates.find((c) => c.kind === "incident" && c.id === linkTo) : undefined;
  const linkOptions: Array<{ incident_id: string; reference: string; incident_type: string; status: string }> = [
    ...activeIncidents,
    ...(chosen && !activeIncidents.some((i) => i.incident_id === chosen.id)
      ? [{ incident_id: chosen.id, reference: chosen.reference, incident_type: chosen.incident_type ?? "", status: chosen.status }]
      : []),
  ];

  return (
    <>
    <section className="fer-panel fer-record" aria-label={`Alert ${a.reference}`}>
      <div className="lcc-detail__head">
        <div className="lcc-detail__titles">
          <p className={`lcc-eyebrow${a.channel === "app_sos" ? " lcc-eyebrow--emergency" : ""}`}>
            {a.channel === "app_sos" ? "SOS from the app" : "Alert"} · received {formatDateTime(a.received_at)}
          </p>
          <h3 className="lcc-detail__title">Alert {a.reference}</h3>
          <span className="fer-badges fer-record__status">
            <StatusBadge status={statusOf(CHANNEL, a.channel)} />
            <StatusBadge status={statusOf(ALERT_STATUS, a.status)} />
          </span>
        </div>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Back to the queue
        </Button>
      </div>
      <StaleNotice {...fresh} error={q.error} what="alert" />
      <FlagBadges map={ALERT_FLAG} flags={a.flags} />
      <p className="fer-clock" role="timer" aria-live="off">
        {isNew ? (fresh.stale ? "Waiting at least" : "Waiting") : "Handled after"} {formatSeconds(waited)} (received {formatDateTime(a.received_at)})
        {fresh.stale ? " – stopped: not refreshed" : ""}
      </p>
      <ProblemAlert error={claim.error ?? useFix.error} />
      <UnknownUnkeyed show={claim.unknown || useFix.unknown} />

      <dl className="fer-dl" aria-label="Caller">
        <Row term="Caller">
          {a.caller.redacted_at ? (
            <span className="fer-muted">Removed under the retention rules ({formatDateTime(a.caller.redacted_at)})</span>
          ) : (
            <>
              {a.caller.display_name ?? "No name"}, {a.caller.phone ?? "no phone number"}
              {a.caller.phone && a.caller.phone_verified === true ? " (verified)" : ""}
              {a.caller.national_subject ? " – national sign-in" : ""}
            </>
          )}
        </Row>
        <Row term="Handler">
          {a.handler_id ? personLabel(a.handler_id, principal.partyId, a.handler_name) : "Nobody has claimed it"}
        </Row>
        {a.incident_type && <Row term="Type given">{names.incidentType(a.incident_type)}</Row>}
        {a.place_description && <Row term="Place described">{a.place_description}</Row>}
        {a.note && <Row term="Note">{a.note}</Row>}
        {!isNew && (
          <Row term="Handled">
            {personLabel(a.handled_by, principal.partyId)}, {formatDateTime(a.handled_at)}
            {a.auth === "local_auth" ? " (FER local sign-in)" : ""}
            {a.dismiss_reason ? ` – dismissed: ${names.dismissReason(a.dismiss_reason)}${a.dismiss_note ? `, “${a.dismiss_note}”` : ""}` : ""}
          </Row>
        )}
        {a.reopened_at && (
          <Row term="Reopened">
            {personLabel(a.reopened_by, principal.partyId)}, {formatDateTime(a.reopened_at)}: {a.reopen_reason}
          </Row>
        )}
      </dl>
      <p className="fer-hint">The caller is shown in full only here, to the control room and the chief; every view is recorded.</p>

      <div className="fer-actions fer-actions--start" role="group" aria-label="Alert actions">
        {isNew && can(principal, "claimAlert") && (
          <Button
            variant={a.handler_id === principal.partyId ? "secondary" : "primary"}
            disabled={claim.isPending || checking}
            onClick={() => (a.handler_id === principal.partyId ? claim.mutate(true) : void startClaim())}
          >
            {a.handler_id === principal.partyId ? "Release (stop handling)" : a.handler_id ? "Take over this alert" : "Claim (I am phoning this caller)"}
          </Button>
        )}
        {isNew && can(principal, "confirmAlert") && (
          <Button onClick={() => setDialog("confirm")}>
            Confirm as a new incident
          </Button>
        )}
        {isNew && can(principal, "linkAlert") && (
          <Button variant="secondary" onClick={() => openLink("")}>
            Link to an existing incident
          </Button>
        )}
        {isNew && can(principal, "dismissAlert") && (
          <Button variant="secondary" onClick={() => setDialog("dismiss")}>
            Dismiss
          </Button>
        )}
        {a.status !== "dismissed" && can(principal, "sendLocationLink") && (
          <Button variant="secondary" onClick={() => setDialog("link")}>
            Send a location link
          </Button>
        )}
        {a.status !== "dismissed" && can(principal, "recordCallback") && (
          <Button variant="secondary" onClick={() => setDialog("callback")}>
            Record a call-back
          </Button>
        )}
        {can(principal, "reopenAlert") && mayReopen(a, principal, now) && (
          <Button variant="secondary" onClick={() => setDialog("reopen")}>
            Reopen (handling mistake)
          </Button>
        )}
        {a.incident_id && can(principal, "viewLiveMap") && (
          <Button variant="secondary" onClick={() => go("board", { incidentId: a.incident_id! })}>
            Open its incident on the board
          </Button>
        )}
      </div>
      {isNew && can(principal, "dismissAlert") && rule !== "ok" && (
        <Notice tone="warning">
          {rule === "callback_required"
            ? "This is an SOS from the app: phone the caller back and record the attempt before dismissing it."
            : "Every call-back found no number to call: only the chief may dismiss this SOS."}
        </Notice>
      )}

      <h4>Location</h4>
      {a.location ? (
        <p>
          <StatusBadge status={statusOf(ACCURACY_CLASS, a.location.accuracy_class)} /> {labelOf(LOCATION_SOURCE, a.location.source)}, within{" "}
          {metres(a.location.accuracy_m)}; Plus Code {a.location.plus_code}; {a.location.in_service_area ? wardName(a.location.ward_id) : "outside every ward"}
          {a.location.description ? ` – ${a.location.description}` : ""}
        </p>
      ) : (
        <Notice tone="warning">No location yet. Send a location link, search a place or drop a pin.</Notice>
      )}
      <FireMap ariaLabel={`Map of alert ${a.reference}: positions received and possible duplicates (tables below)`} data={mapData} onPick={(kind, id) => kind === "candidate" && pickCandidate(id)} height={340} />
      <Legend keys={["fix", "candidate"]} inline />
      {a.fixes.length > 0 && (
        <div className="fer-scroll">
          <table className="lcc-table" aria-label="Positions received">
            <thead>
              <tr>
                <th scope="col">From</th>
                <th scope="col">Within</th>
                <th scope="col">Accuracy</th>
                <th scope="col">Plus Code</th>
                <th scope="col">Position</th>
                <th scope="col">Received</th>
                <th scope="col">
                  <span className="lcc-sr-only">Use</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {a.fixes.map((x) => (
                <tr key={x.fix_id}>
                  <th scope="row">{labelOf(LOCATION_SOURCE, x.source)}</th>
                  <td>{metres(x.accuracy_m)}</td>
                  <td>
                    <StatusBadge status={statusOf(ACCURACY_CLASS, x.accuracy_class)} />
                  </td>
                  <td>{x.plus_code}</td>
                  <td>{lonLat(x.lon, x.lat)}</td>
                  <td>{formatDateTime(x.received_at)}</td>
                  <td>
                    {a.location?.fix_id === x.fix_id ? (
                      <Badge tone="success" label="In use" />
                    ) : (
                      isNew &&
                      can(principal, "setAlertLocation") && (
                        <Button variant="secondary" size="sm" disabled={useFix.isPending} onClick={() => useFix.mutate(x.fix_id)}>
                          Use this position
                        </Button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isNew && can(principal, "setAlertLocation") && (
        <Button variant="secondary" onClick={() => setDialog("setLocation")}>
          Set the location from a place or a pin
        </Button>
      )}

      <h4>Possible duplicates ({a.duplicate_candidates.length})</h4>
      {a.duplicate_candidates.length === 0 ? (
        <p className="fer-muted">No open incident or unhandled alert nearby in the time window, and no other alert from this number.</p>
      ) : (
        <div className="fer-scroll">
          <table className="lcc-table" aria-label="Possible duplicates">
            <thead>
              <tr>
                <th scope="col">Reference</th>
                <th scope="col">What</th>
                <th scope="col">Distance</th>
                <th scope="col">Same phone</th>
                <th scope="col">At</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="lcc-sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {a.duplicate_candidates.map((c) => (
                <tr key={`${c.kind}:${c.id}`}>
                  <th scope="row">{c.reference}</th>
                  <td>
                    {c.kind === "incident" ? "Incident" : "Alert"}
                    {c.incident_type ? ` – ${names.incidentType(c.incident_type)}` : ""}
                    {c.severity ? ` (${statusOf(SEVERITY, c.severity).label})` : ""}
                  </td>
                  <td>{c.distance_m == null ? "No location" : metres(c.distance_m)}{onMap.has(c.id) ? "" : " (not on the map)"}</td>
                  <td>{c.same_phone ? "Yes" : "No"}</td>
                  <td>{formatDateTime(c.at)}</td>
                  <td>{c.kind === "incident" ? statusOf(INCIDENT_STATUS, c.status).label : statusOf(ALERT_STATUS, c.status).label}</td>
                  <td className="fer-row-actions">
                    {c.kind === "incident" && isNew && can(principal, "linkAlert") && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openLink(c.id)}
                      >
                        Link to {c.reference}
                      </Button>
                    )}
                    {c.kind === "alert" && (
                      <Button variant="secondary" size="sm" onClick={() => onOpenAlert(c.id)}>
                        Open alert {c.reference}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h4>Location links</h4>
      {a.location_links.length === 0 ? (
        <p className="fer-muted">None sent.</p>
      ) : (
        <ul className="fer-plain-list" aria-label="Location links">
          {a.location_links.map((l) => (
            <li key={l.link_id}>
              To {l.phone}, {formatDateTime(l.sent_at)}; works until {formatDateTime(l.expires_at)}; {l.fixes} position(s) received
              {l.opened_at ? `; opened ${formatDateTime(l.opened_at)}` : ""}.{" "}
              {l.delivery.map((d, i) => (
                <span key={i}>
                  {labelOf(MESSAGE_CHANNEL, d.channel)} by {labelOf(MESSAGE_PATH, d.path)}: <StatusBadge status={statusOf(MESSAGE_STATUS, d.status)} />{" "}
                </span>
              ))}
            </li>
          ))}
        </ul>
      )}

      <h4>Call-backs</h4>
      {a.callback_attempts.length === 0 ? (
        <p className="fer-muted">No call-back recorded.</p>
      ) : (
        <ul className="fer-plain-list" aria-label="Call-backs">
          {a.callback_attempts.map((c, i) => (
            <li key={i}>
              {formatDateTime(c.attempted_at)}: {labelOf(CALLBACK_OUTCOME, c.outcome)} ({personLabel(c.by, principal.partyId)}){c.note ? ` – ${c.note}` : ""}
            </li>
          ))}
        </ul>
      )}

      {a.messages.length > 0 && (
        <>
          <h4>Messages</h4>
          <ul className="fer-plain-list" aria-label="Messages">
            {a.messages.map((m) => (
              <li key={m.message_id}>
                {labelOf(MESSAGE_CHANNEL, m.channel)} {formatDateTime(m.received_at)}: {m.text ?? <span className="fer-muted">(text removed)</span>}
                {m.has_pin ? " (with a location pin)" : ""}
              </li>
            ))}
          </ul>
        </>
      )}
      <h4>Photos</h4>
      <FileLinks ids={a.photo_file_ids} noun="Photo" />

      {dialog === "confirm" && (
        <ConfirmDialog
          alert={a}
          context={mapData}
          onClose={() => setDialog(null)}
          onConfirmed={(inc) => {
            setDialog(null);
            if (can(principal, "viewLiveMap")) go("board", { incidentId: inc.incident_id });
          }}
        />
      )}
      {dialog === "link" && <LocationLinkDialog alert={a} onClose={() => setDialog(null)} />}
      {dialog === "setLocation" && <SetLocationDialog alert={a} context={mapData} onClose={() => setDialog(null)} />}
      {dialog === "callback" && (
        <ActionDialog
          title={`Record a call-back for ${a.reference}`}
          fields={[
            { name: "outcome", label: "Outcome", kind: "select", required: true, options: optionsOf(CALLBACK_OUTCOME) },
            { name: "attempted_at", label: "When (Lusaka time; default now)", kind: "datetime" },
            { name: "note", label: "Note", kind: "textarea", maxLength: 1000 },
          ]}
          submitLabel="Record the call-back"
          build={(v) => {
            const body: Record<string, unknown> = { outcome: v.outcome };
            if (v.attempted_at) {
              const at = lusakaToUtc(v.attempted_at);
              if (!at) return { error: "Give the time as a date and time." };
              body.attempted_at = at;
            }
            if (v.note?.trim()) body.note = v.note.trim();
            return { body };
          }}
          write={(api, body) => endpoints.recordCallback(api, a.alert_id, body as never)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "dismiss" && (
        <ActionDialog
          title={`Dismiss alert ${a.reference}`}
          intro={
            rule !== "ok" ? (
              <Notice tone="warning">
                {rule === "callback_required" ? "Record a call-back attempt first: the service will refuse the dismissal." : "Only the chief may dismiss this SOS."}
              </Notice>
            ) : undefined
          }
          fields={[
            {
              name: "reason_category",
              label: "Why",
              kind: "select",
              required: true,
              options: (names.staff?.dismiss_reasons ?? []).map((r) => [r.key, names.dismissReason(r.key)] as const),
            },
            { name: "note", label: "Note (at least 3 characters)", kind: "textarea", required: true, maxLength: 2000 },
          ]}
          submitLabel="Dismiss the alert"
          build={(v) => {
            const e = reasonError(v.note ?? "", "A note");
            return e ? { error: e } : { body: { reason_category: v.reason_category, note: v.note.trim() } };
          }}
          write={(api, body) => endpoints.dismissAlert(api, a.alert_id, body as never)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "reopen" && (
        <ActionDialog
          title={`Reopen alert ${a.reference}`}
          intro={<p>The alert returns to the queue as new. An incident it was linked to is kept (only this alert is unlinked from it).</p>}
          fields={[{ name: "reason", label: "Why it was handled by mistake (at least 3 characters)", kind: "textarea", required: true, maxLength: 2000 }]}
          submitLabel="Reopen the alert"
          build={(v) => {
            const e = reasonError(v.reason ?? "");
            return e ? { error: e } : { body: { reason: v.reason.trim() } };
          }}
          write={(api, body) => endpoints.reopenAlert(api, a.alert_id, body as never)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "linkIncident" && (
        <ActionDialog
          title={`Link alert ${a.reference} to an incident`}
          intro={<p>The caller then follows that incident. Its positions are offered on the incident when more accurate.</p>}
          initial={{ incident_id: linkTo }}
          fields={[
            {
              name: "incident_id",
              label: "Incident",
              kind: "select",
              required: true,
              options: linkOptions.map((i) => [i.incident_id, `${i.reference} – ${names.incidentType(i.incident_type)} (${statusOf(INCIDENT_STATUS, i.status).label})`] as const),
            },
            { name: "note", label: "Note", kind: "textarea", maxLength: 2000 },
          ]}
          submitLabel="Link the alert"
          build={(v) => ({ body: v.note?.trim() ? { incident_id: v.incident_id, note: v.note.trim() } : { incident_id: v.incident_id } })}
          write={(api, body) => endpoints.linkAlert(api, a.alert_id, body as never)}
          onClose={() => {
            setDialog(null);
            setLinkTo("");
          }}
        />
      )}
    </section>
    {takeover && (
      <Dialog title={`Alert ${a.reference} is being handled`} onClose={() => setTakeover(null)}>
        <div className="fer-form">
          <p>
            <strong>{takeover.name}</strong> is handling this alert (phoning the caller). Take it over only if you have agreed it with them.
          </p>
          <div className="fer-actions">
            <Button variant="secondary" onClick={() => setTakeover(null)}>
              Leave it with them
            </Button>
            <Button
              onClick={() => {
                setTakeover(null);
                claim.mutate(false);
              }}
            >
              Take over
            </Button>
          </div>
        </div>
      </Dialog>
    )}
    </>
  );
}

function SetLocationDialog({ alert, onClose, context }: { alert: Alert; onClose: () => void; context: MapData }) {
  const [loc, setLoc] = useState<LocationInput | null>(null);
  const w = useWrite((api, body: LocationInput) => endpoints.setAlertLocation(api, alert.alert_id, body));
  return (
    <Dialog title={`Set the location of ${alert.reference}`} onClose={onClose}>
      <div className="fer-form">
        <LocationPicker value={loc} onChange={setLoc} context={context} />
        <ProblemAlert error={w.error} />
        <UnknownUnkeyed show={w.unknown} />
        <div className="fer-actions">
          <Button variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button disabled={!loc || w.isPending} onClick={() => loc && w.mutate(loc, { onSuccess: onClose })}>
            Set this location
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
